import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import {
  CustomInput,
  CustomNumberInput,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import {
  SimulationDatasetApiProvider,
  useSimulationDatasetApi,
} from '../Services/SimulationDatasetApiProvider';
import { EvalConversationMessageInput } from '../Services/SimulationDatasetApiProviderTypes';
import { IN, OUT } from './SimulationDatasetHelpers';
import './SimulationDatasetConversation.css';

interface EditableMessage extends EvalConversationMessageInput {
  uid: string;
}

let uidCounter = 0;
const nextUid = (): string => `msg-${Date.now()}-${uidCounter++}`;

function SimulationDatasetTestDetailContent() {
  const { organizationId, datasetKey, conversationKey } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getConversation, createConversation, updateConversation } = useSimulationDatasetApi();

  const isNew = !conversationKey || conversationKey === 'new-conversation';

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [expected, setExpected] = useState<string>('');
  const [minScore, setMinScore] = useState<number>(85);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [messages, setMessages] = useState<EditableMessage[]>([]);

  const chatRef = useRef<HTMLDivElement>(null);
  const scrollToBottomRef = useRef<boolean>(false);

  useEffect(() => {
    if (scrollToBottomRef.current && chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
      scrollToBottomRef.current = false;
    }
  }, [messages.length]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (isNew || !datasetKey || !conversationKey) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getConversation(datasetKey, conversationKey);
        if (!data) {
          navigate('/404');
          return;
        }
        setName(data.name || '');
        setExpected(data.reference_answer || '');
        setMinScore(data.min_score ?? 85);
        setIsActive(data.is_active ?? true);
        setMessages(
          (data.messages || []).map((m) => ({
            uid: nextUid(),
            direction: m.direction,
            text: m.text || '',
            order_index: m.order_index,
          })),
        );
      } catch (error) {
        console.error('Error fetching test:', error);
        showAlert('fail', 'Failed to load test');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [datasetKey, conversationKey, isNew]);

  const addMessage = (direction: string): void => {
    scrollToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      { uid: nextUid(), direction, text: '', order_index: prev.length },
    ]);
  };

  const updateMessageText = (uid: string, text: string): void => {
    setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, text } : m)));
  };

  const toggleMessageDirection = (uid: string): void => {
    setMessages((prev) =>
      prev.map((m) => (m.uid === uid ? { ...m, direction: m.direction === IN ? OUT : IN } : m)),
    );
  };

  const removeMessage = (uid: string): void => {
    setMessages((prev) => prev.filter((m) => m.uid !== uid));
  };

  const onCancel = (): void => {
    navigate(`/organizations/${organizationId}/simulation-datasets/${datasetKey}`);
  };

  const onSave = async (): Promise<void> => {
    if (!expected.trim()) {
      showAlert('fail', 'Expected answer is required!');
      return;
    }
    if (!datasetKey) return;

    try {
      setSaving(true);
      const payloadMessages: EvalConversationMessageInput[] = messages.map((m, index) => ({
        direction: m.direction,
        text: m.text,
        order_index: index,
      }));

      if (isNew) {
        await createConversation(datasetKey, {
          reference_answer: expected.trim(),
          messages: payloadMessages,
          name: name.trim() || null,
          min_score: minScore,
        });
      } else if (conversationKey) {
        await updateConversation(datasetKey, conversationKey, {
          reference_answer: expected.trim(),
          messages: payloadMessages,
          name: name.trim() || null,
          min_score: minScore,
          is_active: isActive,
        });
      }

      navigate(`/organizations/${organizationId}/simulation-datasets/${datasetKey}`, {
        state: {
          successMessage: isNew ? 'Test created successfully' : 'Test updated successfully',
        },
      });
    } catch (error) {
      console.error('Error saving test:', error);
      showAlert('fail', 'Failed to save test');
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{isNew ? 'Test — Create' : 'Test — Update'}</h2>

          <div className="gap d-flex flex-column">
            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Enter the test name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="test-name"
                    label="Name"
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(_: string, value: string) => setName(value)}
                  />
                </div>
              </div>
            </div>

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="sim-conv-section-title">Input:</div>
              <div className="sim-chat" ref={chatRef}>
                {messages.length === 0 && (
                  <div className="sim-chat-empty">
                    No messages yet. Add Human / AI turns to build the conversation.
                  </div>
                )}
                {messages.map((m) => (
                  <div key={m.uid} className={`sim-msg ${m.direction === IN ? 'in' : 'out'}`}>
                    <div className="sim-msg-head">
                      <button
                        type="button"
                        className="sim-msg-role"
                        onClick={() => toggleMessageDirection(m.uid)}
                        title="Click to switch role"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                      >
                        {m.direction === IN ? 'Human' : 'AI'}
                      </button>
                      <button
                        type="button"
                        className="sim-msg-remove"
                        onClick={() => removeMessage(m.uid)}
                        title="Remove message"
                      >
                        ✕
                      </button>
                    </div>
                    <textarea
                      value={m.text}
                      placeholder="Message text"
                      onChange={(e) => updateMessageText(m.uid, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div className="sim-chat-actions">
                <Button variant="secondary" size="medium" onClick={() => addMessage(IN)}>
                  + Human
                </Button>
                <Button variant="secondary" size="medium" onClick={() => addMessage(OUT)}>
                  + AI
                </Button>
              </div>
            </div>

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="sim-conv-section-title">Expected:</div>
              <div className="small-font" style={{ marginBottom: 8 }}>
                The expected answer the assistant&apos;s response is scored against.
              </div>
              <CustomTextarea
                id="test-expected"
                addedText={expected}
                height="120px"
                onChange={(value: string) => setExpected(value)}
              />
            </div>

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Min score</div>
                  <div className="small-font">Pass threshold (0-100)</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomNumberInput
                    id="test-min-score"
                    label="Min score"
                    value={minScore}
                    min={0}
                    max={100}
                    onChange={(value: number) => setMinScore(value)}
                  />
                </div>
              </div>

              {!isNew && (
                <div className="input-row input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Active</div>
                    <div className="small-font">Enable or disable this test</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomToggle
                      id="test-active"
                      label={isActive ? 'Active' : 'Inactive'}
                      checked={isActive}
                      onChange={(_id: string, value: boolean) => setIsActive(value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={saving} onClick={onSave}>
              {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
            </Button>
            <Button variant="danger" size="medium" disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SimulationDatasetConversationDetail() {
  return (
    <ConfirmationProvider>
      <SimulationDatasetApiProvider>
        <SimulationDatasetTestDetailContent />
      </SimulationDatasetApiProvider>
    </ConfirmationProvider>
  );
}

export default SimulationDatasetConversationDetail;
